import { Fragment, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { ProviderRequestLogPayload, ProviderRequestLogsPage, providersApi } from '../api/providers';

type PayloadState =
  | { status: 'loading' }
  | { status: 'loaded'; payload: ProviderRequestLogPayload }
  | { status: 'error'; message: string };

type Props = {
  providerId: string;
};

const OUTCOMES = ['', 'SUCCESS', 'BUSINESS_ERROR', 'UNKNOWN'];

export default function ProviderRequestLogPanel({ providerId }: Props) {
  const [data, setData] = useState<ProviderRequestLogsPage | null>(null);
  const [operation, setOperation] = useState('');
  const [outcome, setOutcome] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openedLogId, setOpenedLogId] = useState<string | null>(null);
  const [payloads, setPayloads] = useState<Record<string, PayloadState>>({});

  useEffect(() => {
    setPayloads({});
    setOpenedLogId(null);
  }, [providerId]);

  const toggleDetails = (logId: string) => {
    if (openedLogId === logId) {
      setOpenedLogId(null);
      return;
    }
    setOpenedLogId(logId);
    const current = payloads[logId];
    if (current && current.status !== 'error') return;

    setPayloads((state) => ({ ...state, [logId]: { status: 'loading' } }));
    providersApi.getRequestLogPayload(providerId, logId)
      .then((payload) => setPayloads((state) => ({ ...state, [logId]: { status: 'loaded', payload } })))
      .catch((err: unknown) => setPayloads((state) => ({
        ...state,
        [logId]: { status: 'error', message: getErrorMessage(err, 'Failed to load request and response bodies.') },
      })));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    providersApi.getRequestLogs(providerId, { operation: operation || undefined, outcome: outcome || undefined, page, pageSize: 20 })
      .then((response) => {
        if (mounted) setData(response);
      })
      .catch((err: any) => {
        if (mounted) setError(err?.response?.data?.message || err?.message || 'Failed to load request log.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [providerId, operation, outcome, page]);

  const resetPage = (setter: (value: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <section className="mt-8 border-t border-gray-200 pt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Provider Request Log</h3>
          <p className="mt-1 text-sm text-gray-500">Outgoing API calls with server-side masked sensitive values.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={operation}
            onChange={(event) => resetPage(setOperation, event.target.value)}
            placeholder="Operation"
            className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={outcome}
            onChange={(event) => resetPage(setOutcome, event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {OUTCOMES.map((value) => <option key={value} value={value}>{value || 'All outcomes'}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading request log…</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Time</th>
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Outcome</th>
                  <th className="px-3 py-2 text-right">HTTP</th>
                  <th className="px-3 py-2 text-right">Duration</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.items || []).map((entry) => (
                  <Fragment key={entry.id}>
                    <tr key={entry.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{new Date(entry.createdAt).toLocaleString()}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">{entry.operation}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          entry.outcome === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                        }`}>{entry.outcome}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{entry.httpStatusCode ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{entry.durationMs} ms</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => toggleDetails(entry.id)}
                          className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
                        >
                          {openedLogId === entry.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {openedLogId === entry.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={6} className="p-3">
                          <div className="mb-3 grid gap-x-6 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                            <span>Request ID: <span className="font-mono text-gray-800">{entry.requestId || '—'}</span></span>
                            <span>Business object: <span className="font-mono text-gray-800">{entry.businessObjectType || '—'} {entry.businessObjectId || ''}</span></span>
                            <span>Business key: <span className="font-mono text-gray-800">{entry.businessKey || '—'}</span></span>
                            <span>Provider error: <span className="font-mono text-gray-800">{entry.providerErrorCode || '—'}</span></span>
                          </div>
                          <PayloadDetails state={payloads[entry.id]} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!data?.items.length && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No provider requests found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(data?.totalPages ?? 0) > 1 && (
            <div className="mt-4 flex items-center justify-end gap-3 text-sm">
              <span className="text-gray-500">Page {data?.page} of {data?.totalPages}</span>
              <button type="button" disabled={!data?.hasPreviousPage} onClick={() => setPage((value) => Math.max(1, value - 1))} className="rounded border px-3 py-1.5 disabled:opacity-40">Previous</button>
              <button type="button" disabled={!data?.hasNextPage} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function PayloadDetails({ state }: { state?: PayloadState }) {
  if (!state || state.status === 'loading') {
    return <p className="text-sm text-gray-500">Loading request and response bodies…</p>;
  }
  if (state.status === 'error') {
    return <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{state.message}</div>;
  }
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Payload label="Request" value={state.payload.requestPayload} />
      <Payload label="Response" value={state.payload.responsePayload} />
    </div>
  );
}

function Payload({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</h4>
      <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{value || '—'}</pre>
    </div>
  );
}
